import {Component, EventEmitter, Input, NO_ERRORS_SCHEMA, OnDestroy, OnInit, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {CheckBoxModule, InputsModule} from '@progress/kendo-angular-inputs';
import {ButtonsModule} from '@progress/kendo-angular-buttons';
import {
  DialogCloseResult,
  DialogContentBase,
  DialogRef,
  DialogService,
  DialogsModule,
  WindowModule,
  WindowRef
} from '@progress/kendo-angular-dialog';
import {NotificationModule, NotificationService} from '@progress/kendo-angular-notification';
import {KENDO_PAGER, PagerModule} from '@progress/kendo-angular-pager';
import {GridModule, PageChangeEvent, PagerPosition, PagerType} from '@progress/kendo-angular-grid';
import {MulticheckFilterComponent} from '../../filters/multicheck-filter/multicheck-filter.component';
import {
  PlacementReview,
  PlacementReviewDomain,
  PlacementReviewDomainNew,
  PlacementReviewDomainType
} from '../../models/models.lib';
import {Observable, Subscription} from 'rxjs';
import {CasiCloseOptions, FormModes} from '../../common/constants.lib';
import {LoggerService} from '../../shared/logger.service';
import {MeService} from '../../auth/me.service';
import {ToastrService} from 'ngx-toastr';
import {faArrowDown, faArrowUp, faEdit, faPlusCircle} from '@fortawesome/free-solid-svg-icons';
import {setTitle} from '../../common/utils.lib';
import {CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor} from '@progress/kendo-data-query';
import {PlacementReviewDomainService} from '../placement-review-domain.service';

@Component({
  selector: 'casi-placement-review-domain-list-win',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    NotificationModule,
    CheckBoxModule,
    KENDO_PAGER,
    PagerModule,
    GridModule,
    MulticheckFilterComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './placement-review-domain-list-win.html',
  styleUrl: './placement-review-domain-list-win.scss'
})
export class PlacementReviewDomainListWin  implements OnInit, OnDestroy {
  private _placementReview: PlacementReview | undefined;

  @Input() title: string = 'Title Not Set';

  @Input({ required: true }) set placementReview(val: PlacementReview) {
    this._placementReview = val;
  }
  get placementReview(): PlacementReview | undefined {
    return this._placementReview;
  };

  @Output() onClose = new EventEmitter<void>();

  private _windowRef: WindowRef | undefined;

  public view: Observable<PlacementReviewDomain[]> = new Observable<PlacementReviewDomain[]>();
  public rawData: PlacementReviewDomain[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: PlacementReviewDomain[] = filterBy(this.rawData, this.filter);

  public currentItem: PlacementReviewDomainType = PlacementReviewDomainNew.create(this.placementReview);

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";
  public pageSize = 10;
  private _subs: Subscription[] = [];


  constructor(
    private logger: LoggerService,
    private notificationService: NotificationService,
    private me: MeService,
    public dataService: PlacementReviewDomainService,
    public faIcons: FaIconLibrary,
    private dialogService: DialogService,
    private toastr: ToastrService
  ) {
    faIcons.addIcons(
      faPlusCircle, faEdit, faArrowUp, faArrowDown
    );
  }

  public sort: SortDescriptor[] = [
    // { field: "sort_order", dir: "asc" }
  ];

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    if (this.placementReview)
      this.dataService.query(this.placementReview.id);

    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:v: ', e);
      this.rawData = e;
      this.loadData();
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }


  public pageChange(event: PageChangeEvent): void {
    this.logger.debug(`pageChange: `, event);
  }

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]).filter(v => v !== null && v !== undefined && v !== '');
  }

  setIncluded(dataItem: any): void {
    dataItem.included = !dataItem.included;
    this.logger.debug('setIncluded:dataItem: ', dataItem);
    this.currentItem = PlacementReviewDomain.create(dataItem);
    this.save(this.currentItem);
  }

  public onCancelAction(): void {
    this.logger.debug(`onCancelAction...`);
    this.onClose.emit();
  }

  private save(dataItem: any): void {
    this.logger.debug('save...', dataItem);
    this.dataService.update(dataItem.id, dataItem)
      .then(resp => {
        this.logger.debug('save:update:resp: ', resp);
        this.notify("Domain Updated");
        if (this.placementReview)
          this.dataService.query(this.placementReview.id);
      })
      .catch(err => {
        this.logger.error('save:update:err: ', err);
        this.notify("Error Updating Domain", true);
      });
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  // private closeWindowRef(): void {
  //   if (this._windowRef) {
  //     this._windowRef.close();
  //     this._windowRef = undefined;
  //   }
  //   this.editWinOpen = false;
  // }

  private loadData(): void {
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }
}


